// ABOUTME: requestVideoFrameCallback pump — the mechanism behind frame-accurate loop wraps.
// ABOUTME: Pure, no React, no player knowledge: feature detection + a self-re-arming per-presented-frame callback.
//
// WHY THIS EXISTS (measured 2026-08-24)
// `timeupdate` samples media time ~4x a second, so a loop boundary detected there fires anywhere
// in a −SEG_EPSILON..+250ms window around the authored wrap point: an early fire cuts the tail
// frames of the cycle, a late fire bleeds frames of whatever follows the loop in the shared media
// file (on MOVEMENT that is the exit rewind's camera pull-back) before an extra-visible snap
// back. At 30fps that is a −1.5..+7.5 frame lottery drawn fresh on every single wrap.
//
// rVFC is the presented-frame event the platform actually offers: the callback runs once per
// frame composited to the screen, with `metadata.mediaTime` carrying that exact frame's
// presentation timestamp. Detecting the boundary there pins the wrap to the authored frame.
//
// The pump is deliberately dumb: it re-arms itself after every presented frame and reports
// upward. What a frame *means* (loop maths, FSM state, which surface is active) stays in
// `useVideoPlayer` — same split as stallGuard: this module owns the mechanism, the player owns
// the policy. Callbacks are per-element and survive src swaps and pauses (a paused element
// simply presents nothing, so an idle pump costs nothing). Browsers stop compositing hidden
// tabs, so rVFC goes quiet there while media time keeps running — the player keeps a coarse
// `timeupdate` backstop armed for exactly that case; this module does not try to solve it.

/** True when the element implements requestVideoFrameCallback (Chrome 83+, Safari 15.4+, Firefox 132+). */
export function supportsFrameCallbacks(video) {
  return !!video && typeof video.requestVideoFrameCallback === 'function';
}

/**
 * Start a self-re-arming presented-frame pump on `video`.
 *
 * @param {HTMLVideoElement} video
 * @param {(now: DOMHighResTimeStamp, metadata: VideoFrameCallbackMetadata) => void} onFrame
 * @returns {(() => void) | null}  stop() — cancels the pending callback and prevents re-arming;
 *                                 null when the element does not support rVFC (caller falls
 *                                 back to timeupdate-only detection).
 */
export function startFramePump(video, onFrame) {
  if (!supportsFrameCallbacks(video)) return null;
  let handle = null;
  let stopped = false;
  const tick = (now, metadata) => {
    if (stopped) return;
    onFrame(now, metadata);
    if (!stopped) handle = video.requestVideoFrameCallback(tick); // re-arm AFTER the report — onFrame may stop the pump
  };
  handle = video.requestVideoFrameCallback(tick);
  return () => {
    stopped = true;
    if (handle !== null && typeof video.cancelVideoFrameCallback === 'function') {
      video.cancelVideoFrameCallback(handle);
    }
  };
}
